/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


/* Collection Color Filter - Async Filtering */
(function() {
  let colorFilterSwiper = null;

  /**
   * Custom color sort order for collection filter - loaded from theme settings
   * Fallback to default order if settings not available
   */
  const COLLECTION_FILTER_COLOR_ORDER = window.themeVariables?.settings?.colorSortOrder || [
    'BLACK',
    'GREY MELANGE',
    'DARK BLUE',
    'ANTHRAZIT',
    'GREY',
    'CREAM WHITE',
    'BROWN',
    'ICE BLUE',
    'ROYAL BLUE',
    'WOOD GREEN',
    'WHITE',
    'RED',
    'BURGUNDY',
    'PURPLE',
    'DARK GREEN',
    'DARK PURPLE',
    'PINK',
    'MINT GREEN',
    'AQUA BLUE',
    'KHAKI',
    'CARAMEL'
  ];

  /**
   * Sort collection filter color swatches by custom order using CSS order property
   * This is MUCH faster than DOM manipulation - no reflows needed!
   */
  function sortCollectionFilterColors() {
    const wrapper = document.querySelector('.collection-color-filter__inner');
    if (!wrapper) return;

    // Get all color swatches
    const swatches = wrapper.querySelectorAll('.collection-color-filter__swatch');

    if (swatches.length === 0) return;

    // Assign CSS order property based on color position in sort order
    swatches.forEach((swatch) => {
      const colorName = swatch.dataset.filterLabel || '';
      let orderIndex = COLLECTION_FILTER_COLOR_ORDER.indexOf(colorName);

      // If not in custom order, place at end with alphabetical offset
      if (orderIndex === -1) {
        // Place undefined colors after the defined ones (starting at 1000)
        orderIndex = 1000 + colorName.charCodeAt(0);
      }

      // Set the CSS order property (flexbox will handle visual ordering)
      swatch.style.order = orderIndex;
    });
  }

  // Initialize Swiper for color filter
  function initColorFilterSlider() {
    const colorFilterWrapper = document.querySelector('.collection-color-filter__wrapper');

    if (!colorFilterWrapper) return;

    // Sort colors using CSS order property (instant, no DOM manipulation)
    sortCollectionFilterColors();

    // Destroy existing Swiper instance if it exists
    if (colorFilterSwiper) {
      colorFilterSwiper.destroy(true, true);
      colorFilterSwiper = null;
    }

    // Initialize new Swiper
    colorFilterSwiper = new Swiper(colorFilterWrapper, {
      slidesPerView: 'auto',
      spaceBetween: 2,
      freeMode: {
        enabled: true,
        momentum: true,
        momentumRatio: 1,
        momentumVelocityRatio: 1,
      },
      navigation: {
        nextEl: '.collection-color-filter__nav-next',
        prevEl: '.collection-color-filter__nav-prev',
      },
      touchRatio: 1,
      touchAngle: 45,
      simulateTouch: true,
      resistance: true,
      resistanceRatio: 0.85,
      breakpoints: {
        480: {
          spaceBetween: 2,
        },
        740: {
          spaceBetween: 2,
        },
        999: {
          spaceBetween: 2,
        }
      }
    });
  }

  // Handle color filter clicks using event delegation on document
  document.addEventListener('click', function(event) {
    const colorSwatch = event.target.closest('.collection-color-filter__swatch');

    if (!colorSwatch) return;

    event.preventDefault();

    // Don't do anything if the swatch is disabled
    if (colorSwatch.classList.contains('is-disabled')) {
      return;
    }

    const colorFilterSlider = document.querySelector('.collection-color-filter');
    if (!colorFilterSlider) return;

    const filterValue = colorSwatch.dataset.colorFilter;
    const filterParam = colorSwatch.dataset.filterParam;

    // Toggle active state
    const isActive = colorSwatch.classList.contains('is-active');

    if (isActive) {
      // Deselect this color
      colorSwatch.classList.remove('is-active');

      // Remove checkmark
      const checkmark = colorSwatch.querySelector('.collection-color-filter__checkmark');
      if (checkmark) {
        checkmark.remove();
      }
    } else {
      // Select this color
      colorSwatch.classList.add('is-active');

      // Add checkmark
      const checkmark = document.createElement('span');
      checkmark.className = 'collection-color-filter__checkmark';
      checkmark.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="8" fill="white"/><path d="M12 5L6.5 10.5L4 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      colorSwatch.querySelector('.collection-color-filter__color').appendChild(checkmark);
    }

    // Build the filter URL with multiple selections
    const currentUrl = new URL(window.location.href);

    // Get all active colors
    const activeSwatches = colorFilterSlider.querySelectorAll('.collection-color-filter__swatch.is-active');

    // Remove existing color filter params
    const params = new URLSearchParams(currentUrl.search);

    // Delete all instances of this filter parameter
    params.delete(filterParam);

    // Add each selected color as a separate parameter
    activeSwatches.forEach(swatch => {
      params.append(filterParam, swatch.dataset.colorFilter);
    });

    currentUrl.search = params.toString();

    // Remove page parameter to start from page 1
    currentUrl.searchParams.delete('page');

    // Trigger the product facet update using the theme's custom element
    const productFacet = document.querySelector('product-facet');
    if (productFacet) {
      // Dispatch the facet criteria changed event
      productFacet.dispatchEvent(new CustomEvent('facet:criteria-changed', {
        bubbles: true,
        detail: { url: currentUrl.toString() }
      }));

      // Update browser URL without reload
      history.pushState({}, '', currentUrl.toString());

      // Trigger the internal re-render method
      if (typeof productFacet._rerender === 'function') {
        productFacet._rerender(currentUrl.toString());
      } else {
        // Fallback: manually fetch and update
        fetch(currentUrl.toString())
          .then(response => response.text())
          .then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Update the main product grid
            const newMain = doc.querySelector('#facet-main');
            const currentMain = document.querySelector('#facet-main');
            if (newMain && currentMain) {
              currentMain.innerHTML = newMain.innerHTML;
            }

            // Update the filters sidebar if present
            const newFilters = doc.querySelector('#facet-filters');
            const currentFilters = document.querySelector('#facet-filters');
            if (newFilters && currentFilters) {
              currentFilters.innerHTML = newFilters.innerHTML;
            }

            // DO NOT update the color filter slider - keep all colors visible always

            // Scroll to the top of the product grid
            const scrollTarget = document.querySelector('.product-facet__meta-bar') ||
                               document.querySelector('.collection-color-filter') ||
                               document.querySelector('#facet-main');
            if (scrollTarget) {
              scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          })
          .catch(error => {
            console.error('Error filtering products:', error);
          });
      }
    } else {
      // If product-facet custom element is not available, do a regular page load
      window.location.href = currentUrl.toString();
    }
  });

  // Handle keyboard navigation for accessibility
  document.addEventListener('keydown', function(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      const colorSwatch = event.target.closest('.collection-color-filter__swatch');
      if (colorSwatch && !colorSwatch.classList.contains('is-disabled')) {
        event.preventDefault();
        colorSwatch.click();
      }
    }
  });

  // Sort colors as early as possible (before Swiper init)
  function applySortingEarly() {
    sortCollectionFilterColors();
  }

  // Watch for color swatches being added to the DOM and sort immediately
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        const wrapper = document.querySelector('.collection-color-filter__inner');
        if (wrapper && wrapper.querySelectorAll('.collection-color-filter__swatch').length > 0) {
          sortCollectionFilterColors();
        }
      }
    });
  });

  // Start observing the document for changes
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      applySortingEarly();
      initColorFilterSlider();
    });
  } else {
    applySortingEarly();
    initColorFilterSlider();
  }

  // Also re-apply sorting after AJAX filter updates
  document.addEventListener('facet:criteria-changed', function() {
    // Use setTimeout to ensure DOM has updated
    setTimeout(sortCollectionFilterColors, 10);
  });
})();


/* custom variant picker  */
function getSiblingWithClass(element, className) {
  if (element.classList.contains(className)) {
    return element;
  }
  let sibling = element.nextElementSibling;

  while (sibling) {
    if (sibling.classList.contains(className)) {
      return sibling;
    }
    sibling = sibling.nextElementSibling;
  }

  sibling = element.previousElementSibling;

  while (sibling) {
    if (sibling.classList.contains(className)) {
      return sibling;
    }
    sibling = sibling.previousElementSibling;
  }
  return null;
}

document.addEventListener("click", function (event) {
  var swatch = event.target.closest(".swatch");

  if (swatch) {
    const container = swatch.closest(".product-container");
    const product_block = container.closest(".product-container-container");

    if (container) {
      var variants = JSON.parse(container.getAttribute("data-variants"));
      var product_url = container.getAttribute("data-url");
      const swatchContainer = swatch.closest(".swatch-container");
      const swatchList = Array.from(
        swatchContainer.querySelectorAll(".swatch")
      );
      swatchList.forEach((s) => s.classList.remove("active"));
      swatch.classList.add("active");

      var selectedOptions = [];
      var optionContainers = container.querySelectorAll(".swatch-container");

      optionContainers.forEach(function (optionContainer) {
        var activeSwatch = optionContainer.querySelector(".swatch.active");
        if (activeSwatch) {
          selectedOptions.push(activeSwatch.getAttribute("data-value"));
        } else {
          selectedOptions.push(null);
        }
      });

      var selectedVariant = findVariant(variants, selectedOptions);
      if (selectedVariant) {
        container.setAttribute("data-variant-selected", selectedVariant.id);
        updateSoldOutStates(container, variants, selectedOptions);

        if (
          swatch.classList.contains("other_opt") &&
          !swatch.classList.contains("soldout_variant")
        ) {
          addProductToCart(selectedVariant.id);
        }

        if (
          swatch.classList.contains("other_opt") &&
          swatch.classList.contains("soldout_variant")
        ) {
          product_block.querySelector(".BIS_trigger").click();
        }
      }
    }
  }
});

function findVariant(variants, selectedOptions) {
  return variants.find(
    (variant) =>
      variant.option1 == selectedOptions[0] &&
      variant.option2 == selectedOptions[1] &&
      variant.option3 == selectedOptions[2]
  );
}

function updateSoldOutStates(container, variants, selectedOptions) {
  const optionContainers = container.querySelectorAll(".swatch-container");

  optionContainers.forEach(function (optionContainer, index) {
    const swatches = optionContainer.querySelectorAll(".swatch");

    swatches.forEach(function (swatch) {
      const value = swatch.getAttribute("data-value");
      const currentSelectedOptions = [...selectedOptions];
      currentSelectedOptions[index] = value;

      const matchingVariant = findVariant(variants, currentSelectedOptions);
      if (matchingVariant && !matchingVariant.available) {
        swatch.classList.add("soldout_variant");
      } else {
        swatch.classList.remove("soldout_variant");
      }
    });
  });
}

function fetchCartData() {
  fetch(`${window.themeVariables.routes.cartUrl}.js`).then(
    async (response2) => {
      const cartContent = await response2.json();
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail: {
            cart: cartContent,
          },
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: true,
          },
        })
      );
    }
  );
}

async function addProductToCart(vid) {
  let formData = {
    items: [
      {
        id: vid,
        quantity: 1,
      },
    ],
  };

  const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(formData),
  })
    .then((response) => {
      fetchCartData();
    })
    .catch((error) => {
      console.error("Error:", error);
    });
}

document.body.addEventListener("click", async function (event) {
  if (event.target.closest(".product-item-atc")) {
    const vid = event.target
      .closest(".product-item-atc")
      .getAttribute("data-vid");
    console.log(vid);
    if (
      event.target.closest(".product-item-atc").classList.contains("open_sizes")
    ) {
      const parentContainer = event.target.closest(
        ".product-container-container"
      );
      const cardSizePicker = parentContainer.querySelector(".card-size-picker");
      cardSizePicker.classList.add("active");
    } else {
      addProductToCart(vid);
    }
  }
});

document.body.addEventListener("click", async function (event) {
  if (event.target.closest(".card-size-picker-close")) {
    document.querySelectorAll(".card-size-picker").forEach((picker) => {
      picker.classList.remove("active");
    });
    return;
  }
});


/* custom variant picker  */

/**
 * ------------------------------------------------------------------------------------------------------------
 * SHOP BY COLOR - DYNAMIC COLOR LOADER
 * ------------------------------------------------------------------------------------------------------------
 *
 * Loads colors from collection.colors.json template using pagination to overcome 50-product limit.
 * Supports both desktop mega menu and mobile drawer navigation.
 */

(function() {
  'use strict';

  // Translation for white color (for white border class)
  const colorWhiteLabel = 'white'; // Adjust if needed based on your locale

  /**
   * Custom color sort order - loaded from theme settings
   * Fallback to default order if settings not available
   */
  const COLOR_SORT_ORDER = window.themeVariables?.settings?.colorSortOrder || [
    'BLACK',
    'GREY MELANGE',
    'DARK BLUE',
    'ANTHRAZIT',
    'GREY',
    'CREAM WHITE',
    'BROWN',
    'ICE BLUE',
    'ROYAL BLUE',
    'WOOD GREEN',
    'WHITE',
    'RED',
    'BURGUNDY',
    'PURPLE',
    'DARK GREEN',
    'DARK PURPLE',
    'PINK',
    'MINT GREEN',
    'AQUA BLUE',
    'KHAKI',
    'CARAMEL'
  ];

  /**
   * Sort colors based on custom order
   */
  function sortColorsByCustomOrder(colors) {
    return colors.sort((a, b) => {
      const indexA = COLOR_SORT_ORDER.indexOf(a.name);
      const indexB = COLOR_SORT_ORDER.indexOf(b.name);

      // If both colors are in the sort order, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only A is in the sort order, it comes first
      if (indexA !== -1) {
        return -1;
      }

      // If only B is in the sort order, it comes first
      if (indexB !== -1) {
        return 1;
      }

      // If neither is in the sort order, sort alphabetically
      return a.name.localeCompare(b.name);
    });
  }

  /**
   * Fetch all colors from a collection using pagination
   */
  async function fetchAllColors(collectionHandle) {
    let allColors = [];
    let currentPage = 1;
    let hasNextPage = true;

    try {
      while (hasNextPage) {
        const url = `/collections/${collectionHandle}?view=colors${currentPage > 1 ? `&page=${currentPage}` : ''}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to fetch colors: ${response.status}`);
        }

        const data = await response.json();

        // Add colors from this page
        if (data.colors && data.colors.length > 0) {
          allColors = allColors.concat(data.colors);
        }

        // Check if there are more pages
        hasNextPage = data.pagination && data.pagination.has_next;
        currentPage++;

        // Safety check to prevent infinite loops (max 100 pages = 25,000 products)
        if (currentPage > 100) {
          console.warn('Reached maximum page limit for color fetching');
          break;
        }
      }

      // Remove duplicates by name (different products may have slight hex variations)
      const uniqueColors = allColors.filter((color, index, self) =>
        index === self.findIndex(c => c.name === color.name)
      );

      // Sort colors using custom order
      return sortColorsByCustomOrder(uniqueColors);
    } catch (error) {
      console.error('Error fetching colors:', error);
      return [];
    }
  }

  /**
   * Render color swatch HTML
   */
  function createColorSwatch(color, collectionHandle, isMobile = false) {
    const colorNameDowncase = color.name.toLowerCase();
    const isWhite = colorNameDowncase === colorWhiteLabel;
    const itemClass = isMobile ? 'mobile-nav__color-item' : 'mega-menu__color-item';
    const swatchClass = isMobile ? 'mobile-nav__color-swatch' : 'mega-menu__color-swatch';
    const labelClass = isMobile ? 'mobile-nav__color-label' : 'mega-menu__color-label';

    const link = document.createElement('a');
    link.href = `/collections/${collectionHandle}?filter.p.m.custom.farbname=${encodeURIComponent(color.name)}`;
    link.className = itemClass + (isWhite ? ' color-swatch--white' : '');
    link.setAttribute('data-color', color.name);

    const swatch = document.createElement('span');
    swatch.className = swatchClass;
    swatch.style.backgroundColor = color.hex;

    const label = document.createElement('span');
    label.className = labelClass;
    label.textContent = color.name;

    link.appendChild(swatch);
    link.appendChild(label);

    return link;
  }

  /**
   * Load colors for desktop mega menu
   */
  async function loadDesktopColors(element) {
    const collectionHandle = element.getAttribute('data-collection-handle');
    if (!collectionHandle) return;

    const colorsGrid = element.querySelector('.mega-menu__colors-grid');
    if (!colorsGrid) return;

    // Fetch colors
    const colors = await fetchAllColors(collectionHandle);

    // Clear loading state
    colorsGrid.innerHTML = '';

    if (colors.length === 0) {
      const errorMsg = document.createElement('div');
      errorMsg.style.cssText = 'grid-column: 1 / -1; text-align: center; padding: 20px;';
      errorMsg.innerHTML = '<span style="color: rgb(var(--text-color)); font-size: 14px;">No colors found in this collection.</span>';
      colorsGrid.appendChild(errorMsg);
      return;
    }

    // Render color swatches
    colors.forEach(color => {
      const swatch = createColorSwatch(color, collectionHandle, false);
      colorsGrid.appendChild(swatch);
    });
  }

  /**
   * Initialize custom slider for mobile color navigation
   */
  function initMobileColorSlider(element, totalColors) {
    const wrapper = element.querySelector('.mobile-nav__colors-wrapper');
    const grid = element.querySelector('.mobile-nav__colors-grid');
    const navPrev = element.querySelector('.mobile-nav__color-nav-prev');
    const navNext = element.querySelector('.mobile-nav__color-nav-next');

    if (!wrapper || !grid || !navPrev || !navNext) return;

    let currentPosition = 0;
    const colsPerView = 2; // Show 2 columns at a time
    const rows = 2; // Fixed 2 rows
    const itemsPerPage = colsPerView * rows; // 4 items per page
    const totalPages = Math.ceil(totalColors / itemsPerPage);
    const totalColumns = Math.ceil(totalColors / rows); // Total columns in grid

    // Update button states
    function updateButtons() {
      navPrev.disabled = currentPosition === 0;
      navNext.disabled = currentPosition >= totalPages - 1;

      navPrev.style.opacity = navPrev.disabled ? '0.3' : '1';
      navNext.style.opacity = navNext.disabled ? '0.3' : '1';
    }

    // Navigate to a specific page
    function goToPage(pageIndex) {
      currentPosition = Math.max(0, Math.min(pageIndex, totalPages - 1));

      // Calculate how many columns to shift
      const columnsToShift = currentPosition * colsPerView;

      // Limit the shift to not go past the last items
      // Maximum shift = total columns - columns per view
      const maxColumnsShift = Math.max(0, totalColumns - colsPerView);
      const actualColumnsToShift = Math.min(columnsToShift, maxColumnsShift);

      // Calculate transform based on column width (approximately 78px per column including gap)
      const columnWidth = 78;
      grid.style.transform = `translateX(-${actualColumnsToShift * columnWidth}px)`;
      grid.style.transition = 'transform 0.3s ease';

      updateButtons();
    }

    // Event listeners
    navPrev.addEventListener('click', () => {
      if (currentPosition > 0) {
        goToPage(currentPosition - 1);
      }
    });

    navNext.addEventListener('click', () => {
      if (currentPosition < totalPages - 1) {
        goToPage(currentPosition + 1);
      }
    });

    // Initialize
    updateButtons();
  }

  /**
   * Load colors for mobile drawer
   */
  async function loadMobileColors(element) {
    const collectionHandle = element.getAttribute('data-collection-handle');
    if (!collectionHandle) return;

    const colorsGrid = element.querySelector('.mobile-nav__colors-grid');
    if (!colorsGrid) return;

    // Fetch colors
    const colors = await fetchAllColors(collectionHandle);

    // Clear loading state
    colorsGrid.innerHTML = '';

    if (colors.length === 0) {
      const errorMsg = document.createElement('div');
      errorMsg.style.cssText = 'text-align: center; padding: 20px; width: 100%;';
      errorMsg.innerHTML = '<span style="color: rgb(var(--text-color)); font-size: 14px;">No colors found in this collection.</span>';
      colorsGrid.appendChild(errorMsg);
      return;
    }

    // Render color swatches directly (no wrapper needed)
    colors.forEach(color => {
      const swatch = createColorSwatch(color, collectionHandle, true);
      colorsGrid.appendChild(swatch);
    });

    // Initialize custom slider for mobile color navigation (2 rows)
    initMobileColorSlider(element, colors.length);
  }

  /**
   * Initialize color loaders on page load
   */
  function initColorLoaders() {
    // Desktop loaders
    const desktopLoaders = document.querySelectorAll('[data-color-loader]');
    desktopLoaders.forEach(loader => {
      loadDesktopColors(loader);
    });

    // Mobile loaders
    const mobileLoaders = document.querySelectorAll('[data-color-loader-mobile]');
    mobileLoaders.forEach(loader => {
      loadMobileColors(loader);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initColorLoaders);
  } else {
    initColorLoaders();
  }
})();